import { useState, useEffect } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LayoutDashboard, Briefcase, FileText, MessageSquare, 
  Users, Settings, LogOut, Menu, X, ChevronDown,
  Image, Star, Bell, Search, Moon, Sun, Layers,
  TrendingUp, ChevronRight, UsersRound, UserCircle, Award, Shield
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [notifications, setNotifications] = useState(3);
  const [currentTime, setCurrentTime] = useState(new Date());
  const { user, logout } = useAuth();
  const location = useLocation();
  
  // État pour les paramètres (logo, nom du site, favicon)
  const [settings, setSettings] = useState({
    site_name: 'WealthTech Pro',
    logo_url: '/logo.jpeg',
    favicon_url: ''
  });

  // Horloge en temps réel
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Charger les paramètres
  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
      const res = await fetch(`${API_URL}/settings`);
      if (res.ok) {
        const result = await res.json();
        if (result.settings) {
          setSettings(prev => ({
            ...prev,
            site_name: result.settings.site_name || 'WealthTech Pro',
            logo_url: result.settings.logo_url || '/logo.jpeg',
            favicon_url: result.settings.favicon_url || ''
          }));

          // ========== FAVICON DYNAMIQUE ==========
          if (result.settings.favicon_url) {
            const existingFavicons = document.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"]');
            existingFavicons.forEach(favicon => favicon.remove());

            const newFavicon = document.createElement('link');
            newFavicon.rel = 'icon';
            newFavicon.type = 'image/x-icon';
            newFavicon.href = result.settings.favicon_url;
            document.head.appendChild(newFavicon);

            const shortcutFavicon = document.createElement('link');
            shortcutFavicon.rel = 'shortcut icon';
            shortcutFavicon.href = result.settings.favicon_url;
            document.head.appendChild(shortcutFavicon);
          }

          // ========== TITRE DYNAMIQUE ==========
          if (result.settings.site_name) {
            document.title = `Admin - ${result.settings.site_name}`;
          }
        }
      }
    } catch (err) {
      console.error('Erreur lors de la récupération des paramètres:', err);
    }
  };

  const menuItems = [
    { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
    { path: '/admin/services', icon: Briefcase, label: 'Services' },
    { path: '/admin/applications', icon: Layers, label: 'Applications' },
    { path: '/admin/testimonials', icon: Star, label: 'Témoignages' },
    { path: '/admin/stats', icon: TrendingUp, label: 'Statistiques' },
    { path: '/admin/features', icon: Award, label: 'Avantages' },
    { path: '/admin/certifications', icon: Shield, label: 'Certifications' },
    { path: '/admin/team', icon: UsersRound, label: 'Équipe' },
    { path: '/admin/contacts', icon: MessageSquare, label: 'Messages', badge: notifications },
    { path: '/admin/medias', icon: Image, label: 'Médiathèque' },
    { path: '/admin/users', icon: UserCircle, label: 'Utilisateurs' },
    { path: '/admin/settings', icon: Settings, label: 'Paramètres' },
  ];

  const formatTime = (date) => {
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour < 12) return 'Bonjour';
    if (hour < 18) return 'Bon après-midi';
    return 'Bonsoir';
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'dark bg-dark-900' : 'bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50'}`}>
      {/* Sidebar Desktop */}
      <motion.aside
        initial={false}
        animate={{ width: sidebarOpen ? 280 : 80 }}
        className={`fixed top-0 left-0 h-full bg-gradient-to-b from-dark-900 via-dark-800 to-primary-900 text-white z-40 hidden lg:block shadow-2xl`}
      >
        {/* Logo Dynamique */}
        <div className="p-6 border-b border-white/10">
          <Link to="/admin" className="flex items-center gap-3">
            {settings.logo_url ? (
              <motion.img
                src={settings.logo_url}
                alt={settings.site_name || 'Logo'}
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.3 }}
                className={`${sidebarOpen ? 'w-20 h-20' : 'w-10 h-10'} object-contain rounded-xl shadow-lg shadow-primary-500/30 transition-all duration-300`}
              />
            ) : (
              <div className={`${sidebarOpen ? 'w-20 h-20' : 'w-10 h-10'} bg-gradient-to-br from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/30`}>
                <span className="text-white font-bold text-2xl">
                  {settings.site_name?.charAt(0) || 'W'}
                </span>
              </div>
            )}
            <AnimatePresence>
              {sidebarOpen && (
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  className="flex flex-col"
                >
                  <span className="font-bold text-lg">{settings.site_name || 'WealthTech Pro'}</span>
                  <span className="text-xs text-white/50">Administration</span>
                </motion.div>
              )}
            </AnimatePresence>
          </Link>
        </div>

        {/* Toggle Button */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="absolute -right-3 top-20 w-6 h-6 bg-primary-600 rounded-full flex items-center justify-center shadow-lg hover:bg-primary-500 transition-colors"
        >
          <ChevronRight className={`w-4 h-4 transition-transform ${sidebarOpen ? 'rotate-180' : ''}`} />
        </button>

        {/* User Profile Mini */}
        <AnimatePresence>
          {sidebarOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="p-4 mx-4 mt-4 bg-white/5 rounded-2xl backdrop-blur"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-primary-400 to-secondary-400 rounded-xl flex items-center justify-center font-bold">
                  {user?.name?.charAt(0) || 'A'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{user?.name || 'Admin'}</p>
                  <p className="text-xs text-white/50 truncate">{user?.role || 'Administrateur'}</p>
                </div>
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Navigation */}
        <nav className="p-4 space-y-1 mt-4 overflow-y-auto max-h-[calc(100vh-350px)]">
          {menuItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.exact}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 group relative overflow-hidden ${
                  isActive
                    ? 'bg-gradient-to-r from-primary-600 to-primary-500 text-white shadow-lg shadow-primary-500/30'
                    : 'text-white/70 hover:bg-white/10 hover:text-white'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <motion.div
                      layoutId="activeTab"
                      className="absolute inset-0 bg-gradient-to-r from-primary-600 to-primary-500 rounded-xl"
                      transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                    />
                  )}
                  <item.icon className={`w-5 h-5 relative z-10 ${isActive ? '' : 'group-hover:scale-110'} transition-transform`} />
                  <AnimatePresence>
                    {sidebarOpen && (
                      <motion.span
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="relative z-10 font-medium"
                      >
                        {item.label}
                      </motion.span>
                    )}
                  </AnimatePresence>
                  {item.badge && sidebarOpen && (
                    <motion.span
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="ml-auto relative z-10 w-6 h-6 bg-red-500 rounded-full text-xs flex items-center justify-center font-bold"
                    >
                      {item.badge}
                    </motion.span>
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Logout Button */}
        <div className="absolute bottom-4 left-4 right-4">
          <button
            onClick={logout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-white/70 hover:bg-red-500/20 hover:text-red-400 transition-all"
          >
            <LogOut className="w-5 h-5" />
            {sidebarOpen && <span>Déconnexion</span>}
          </button>
        </div>
      </motion.aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-dark-900 text-white z-40 flex items-center justify-between px-4 shadow-lg">
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-2 hover:bg-white/10 rounded-lg transition-colors"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
        
        {/* Logo Mobile Dynamique */}
        <Link to="/admin" className="flex items-center gap-2">
          {settings.logo_url ? (
            <img
              src={settings.logo_url}
              alt={settings.site_name || 'Logo'}
              className="w-8 h-8 object-contain rounded-lg"
            />
          ) : (
            <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center">
              <span className="text-white font-bold">W</span>
            </div>
          )}
          <span className="font-bold">{settings.site_name || 'WealthTech Pro'}</span>
        </Link>

        <div className="w-10"></div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: -300 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -300 }}
            className="lg:hidden fixed inset-0 z-30 bg-dark-900 pt-16"
          >
            <nav className="p-4 space-y-2 overflow-y-auto max-h-[calc(100vh-100px)]">
              {menuItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.exact}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                      isActive
                        ? 'bg-primary-600 text-white'
                        : 'text-white/70 hover:bg-white/10'
                    }`
                  }
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.label}</span>
                  {item.badge && (
                    <span className="ml-auto w-6 h-6 bg-red-500 rounded-full text-xs flex items-center justify-center font-bold">
                      {item.badge}
                    </span>
                  )}
                </NavLink>
              ))}
              <button
                onClick={logout}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-white/70 hover:bg-red-500/20 hover:text-red-400"
              >
                <LogOut className="w-5 h-5" />
                <span>Déconnexion</span>
              </button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className={`transition-all duration-300 ${sidebarOpen ? 'lg:ml-[280px]' : 'lg:ml-[80px]'} pt-16 lg:pt-0`}>
        {/* Top Bar */}
        <header className="sticky top-0 z-20 bg-white/80 backdrop-blur-xl border-b border-dark-100 px-6 py-4 hidden lg:block">
          <div className="flex items-center justify-between">
            <div>
              <motion.h2 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-2xl font-bold text-dark-900"
              >
                {getGreeting()}, {user?.name?.split(' ')[0] || 'Admin'} 👋
              </motion.h2>
              <p className="text-dark-500 text-sm">{formatDate(currentTime)}</p>
            </div>

            <div className="flex items-center gap-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-dark-400" />
                <input
                  type="text"
                  placeholder="Rechercher..."
                  className="pl-10 pr-4 py-2 bg-dark-50 rounded-xl border-0 focus:ring-2 focus:ring-primary-500 w-64 transition-all focus:w-80"
                />
              </div>

              {/* Time */}
              <div className="px-4 py-2 bg-gradient-to-r from-primary-50 to-secondary-50 rounded-xl">
                <p className="text-primary-700 font-mono font-bold">{formatTime(currentTime)}</p>
              </div>

              {/* Notifications */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="relative p-3 bg-dark-50 rounded-xl hover:bg-dark-100 transition-colors"
              >
                <Bell className="w-5 h-5 text-dark-600" />
                {notifications > 0 && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold"
                  >
                    {notifications}
                  </motion.span>
                )}
              </motion.button>

              {/* Dark Mode Toggle */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setDarkMode(!darkMode)}
                className="p-3 bg-dark-50 rounded-xl hover:bg-dark-100 transition-colors"
              >
                {darkMode ? <Sun className="w-5 h-5 text-yellow-500" /> : <Moon className="w-5 h-5 text-dark-600" />}
              </motion.button>

              {/* Profile */}
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="flex items-center gap-3 pl-4 border-l border-dark-200"
              >
                <div className="text-right">
                  <p className="font-medium text-dark-900">{user?.name || 'Admin'}</p>
                  <p className="text-xs text-dark-500">{user?.role || 'Administrateur'}</p>
                </div>
                <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center text-white font-bold shadow-lg shadow-primary-500/30">
                  {user?.name?.charAt(0) || 'A'}
                </div>
              </motion.div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-6">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Outlet />
          </motion.div>
        </div>
      </main>
    </div>
  );
}

export default AdminLayout;